<template>
  <Modal
    id="modal_listes_saisie_rapide"
    @change="!$event && hide()"
  >
    <template #title>
      {{ modalTitle }}
    </template>
    <template #subtitle>
      {{ modalSubtitle }}
    </template>
    <template #content>
      <template v-if="!modeFile">
        <table class="text-medium">
          <thead>
            <tr>
              <th
                v-for="item in tableHeader"
                :key="item.key"
              >
                <component
                  :is="item.key === 'ean_ref' ? 'div' : 'button'"
                  class="button filter-button"
                  @click="sortOffers(item.key)"
                >
                  <UisSorting
                    v-if="item.key !== 'ean_ref'"
                    size="16"
                  />
                  <span class="text-small">{{ item.name }}</span>
                </component>
              </th>
            </tr>
          </thead>
          <tbody>
            <template
              v-for="ligne, index in lignes"
              :key="index"
            >
              <tr :class="{ 'ligne-indisponible': isLigneIndisponible(ligne), }">
                <td>
                  <SelectClassic
                    v-model="ligne.offre"
                    :label="$t('produit.ean-ref')"
                    status="required"
                    :options="options"
                    inline
                    select-on-enter
                    :internal-search="false"
                    :options-are-loading="optionsAreLoading"
                    @search="fetchOffres($event)"
                    @update:modelValue="handleSelect(ligne, index)"
                  />
                </td>
                <td>
                  <InputClassic
                    v-model="ligne.quantite"
                    :label="$t('general.quantite')"
                    status="required"
                    type="number"
                    min="1"
                    inline
                  />
                </td>
                <td>{{ ligne.offre?.libelle || "-" }}</td>
                <td>
                  <template v-if="ligne.offre?.prix_public">
                    {{ moneyFilter(ligne.offre.prix_public) }}{{ "\xa0" }}€
                  </template>
                  <template v-else>
                    -
                  </template>
                </td>
                <td>
                  <div class="dispo-cell">
                    <span
                      class="circle"
                      :class="ligne.offre?.dispo"
                    />
                    <span
                      v-if="ligne.offre?.dispo_libelle"
                      class="text-small"
                      :class="`text-${ligne.offre.dispo}`"
                    >
                      {{ ligne.offre.dispo_libelle }}
                    </span>
                  </div>
                </td>
                <td>
                  <ButtonClassic
                    variant="solid"
                    color="primary"
                    icon="left"
                    size="small"
                    @click="deleteLine(index)"
                  >
                    <template #left-icon>
                      <UilTrashAlt />
                    </template>
                  </ButtonClassic>
                </td>
              </tr>
              <!-- Si le produit n'est plus disponible et a été remplacé -->
              <tr
                v-if="ligne.offre?.eanBase"
                :key="`additional_${index}`"
              >
                <td :colspan="tableHeader.length">
                  {{ $t(
                    "produit.ean-x-plus-disponible-remplace-par-nouvelle-version",
                    { ean: ligne.offre?.eanBase, }
                  ) }}
                </td>
              </tr>
              <!-- Si le produit est indisponible et bloque donc la création de la liste -->
              <tr
                v-if="isLigneIndisponible(ligne)"
                :key="`unavailable_${index}`"
                class="ligne-indisponible"
              >
                <td :colspan="tableHeader.length + 1">
                  <span class="text-small text-red">
                    <UisExclamationTriangle size="16" />
                    {{ $t("liste.ligne-indispo-a-supprimer-pour-creer-liste") }}
                  </span>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        <div class="add-line-buttons">
          <span>
            {{ $t('liste.ajouter-des-lignes') }} :
          </span>
          <ButtonClassic
            variant="ghost"
            color="primary"
            label="1"
            icon="left"
            @click="lignes = [...lignes, {}, ]"
          >
            <template #left-icon>
              <UilPlusCircle />
            </template>
          </ButtonClassic>
          <ButtonClassic
            variant="ghost"
            color="primary"
            label="5"
            icon="left"
            @click="lignes = [...lignes, {}, {}, {}, {}, {}, ]"
          >
            <template #left-icon>
              <UilPlusCircle />
            </template>
          </ButtonClassic>
        </div>
      </template>
      <template v-else>
        <div class="file-mode">
          <template v-if="importRecap">
            <div class="import-recap">
              <div class="import-recap__icon-wrapper">
                <UilBookmark
                  size="28"
                  class="import-recap__icon"
                />
                <span class="import-recap__badge">
                  <UisCheckCircle size="20" />
                </span>
              </div>
              <div class="import-recap__body">
                <h2 class="import-recap__title">
                  {{ $t('liste.resultat-de-l-import') }}
                </h2>
                <p class="import-recap__stat import-recap__stat--success">
                  <UisCheckCircle size="18" />
                  <strong>{{ importRecap.importedCount }}</strong>&nbsp;{{ $t('liste.lignes-importees') }}
                </p>
                <p
                  v-if="importRecap.notFoundCount"
                  class="import-recap__stat import-recap__stat--warning"
                >
                  <UisExclamationTriangle size="18" />
                  <strong>{{ importRecap.notFoundCount }}</strong>&nbsp;{{ $t('liste.ean-non-trouve') }}
                </p>
                <p
                  class="import-recap__contact"
                  v-html="$t('liste.trop-ean-non-reconnus-contact', { mail: mailLde, })"
                />
              </div>
              <div
                v-if="importRecap.notFoundEans.length"
                class="import-recap__table-wrapper"
              >
                <table class="import-recap__table">
                  <thead>
                    <tr>
                      <th />
                      <th>EAN</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(ean, idx) in importRecap.notFoundEans"
                      :key="ean"
                    >
                      <td>{{ idx + 1 }}</td>
                      <td>{{ ean }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </template>
          <template v-else>
            <p class="file-mode__instruction text-medium">
              {{ $t('liste.fichier-doit-contenir-colonnes-ean-quantite') }}
            </p>
            <div
              class="file-mode__dropzone"
              :class="{ 'file-mode__dropzone--active': isDragOver, }"
              @dragover.prevent="handleDragOver"
              @dragleave.prevent="handleDragLeave"
              @drop.prevent="handleDrop"
              @click="openFilePicker"
            >
              <img
                class="file-mode__illustration"
                :src="excelRapideImage"
                alt="Exemple de fichier Excel"
              />
              <p class="text-medium">
                {{ $t('liste.glissez-deposez-fichier') }}
              </p>
              <p class="text-small">
                {{ $t('liste.cliquez-pour-selectionner-fichier') }}
              </p>
              <p
                v-if="selectedFile"
                class="file-mode__filename text-small"
              >
                {{ $t('liste.fichier-selectionne-x', { name: selectedFile.name, }) }}
              </p>
              <input
                ref="fileInput"
                class="file-mode__input"
                type="file"
                accept=".csv,.xlsx"
                @change="handleFileInput"
              />
            </div>
          </template>
        </div>
      </template>
    </template>
    <template #footer>
      <ButtonClassic
        v-if="!(modeFile && importRecap)"
        variant="ghost"
        icon="left"
        color="secondary"
        :label="$t('action.annuler')"
        @click="hide"
      >
        <template #left-icon>
          <UilTimesCircle />
        </template>
      </ButtonClassic>
      <ButtonClassic
        v-if="modeFile && importRecap && createdList"
        variant="solid"
        color="primary"
        :label="$t('action.voir-liste')"
        icon="left"
        @click="goToCreatedList"
      >
        <template #left-icon>
          <UilListOl />
        </template>
      </ButtonClassic>
      <ButtonClassic
        v-if="!(modeFile && importRecap)"
        variant="ghost"
        color="primary"
        :label="modeFile ? $t('action.saisir-par-ean') : $t('action.importer-fichier')"
        :disabled="isImpersonating || isCreatingList"
        icon="left"
        @click="toggleMode"
      >
        <template #left-icon>
          <component :is="modeFile ? 'UilListOl' : 'UilFileAlt'" />
        </template>
      </ButtonClassic>
      <ButtonClassic
        v-if="!(modeFile && importRecap)"
        variant="solid"
        color="secondary"
        :label="$t('liste.creer-liste')"
        :disabled="isImpersonating || isCreatingList"
        icon="left"
        @click="createList()"
      >
        <template #left-icon>
          <UilPlusCircle />
        </template>
      </ButtonClassic>
    </template>
  </Modal>
</template>

<script>
import { mapGetters } from "vuex";

import {
  ButtonClassic,
  SelectClassic,
  InputClassic,
  Modal,
  moneyFilter,
} from "@lde/core_lde_vue";
import {
  UilTrashAlt, UilPlusCircle, UilTimesCircle, UilFileAlt, UilListOl, UilBookmark
} from "@lde/vue-unicons";
import {
  UisSorting,
  UisCheckCircle,
  UisExclamationTriangle,
} from "@lde/vue-unicons-solid";

import mixinDisponibilite from "@/mixins/mixinDisponibilite";
import mixinToListe from "@/mixins/mixinToListe";
import mixinAssistance from "@/mixins/mixinAssistance";
import Api from "@/modules/axios";
import excelRapideImageDe from "@/assets/media/img/excel_rapide_de.png";
import excelRapideImageFr from "@/assets/media/img/excel_rapide_fr.png";

/**
 * Modale affichée lors de la création d'une liste à partir de lignes de liste.
 */
export default {
  name: "ModalListesSaisieRapide",
  components: {
    Modal,
    ButtonClassic,
    InputClassic,
    SelectClassic,
    UilTrashAlt,
    UilPlusCircle,
    UilTimesCircle,
    UisSorting,
    UisCheckCircle,
    UisExclamationTriangle,
    UilFileAlt,
    UilListOl,
    UilBookmark
  },
  mixins: [mixinDisponibilite, mixinToListe, mixinAssistance],
  props: {
    /**
     * Nom de la liste.
     */
    listName: {
      type: String,
      default: null,
    },
  },
  emits: [
    /**
     * Déclenché à l'ajout de la liste.
     * @param {Object} - La nouvelle liste créée.
     */
    "submit",
  ],
  data() {
    return {
      tableHeader: [
        { key: "ean_ref", name: this.$t("produit.ean-ref") },
        { key: "quantite", name: this.$t("general.quantite") },
        { key: "libelle", name: this.$t("general.designation") },
        { key: "prix_public", name: this.$t("prix.prix-public") },
        { key: "dispo", name: this.$t("general.dispo") },
      ],
      lastSort: {},
      lignes: [{}, {}, {}, {}],
      options: [],
      optionsAreLoading: false,
      isCreatingList: false,
      modeFile: false,
      selectedFile: null,
      isDragOver: false,
      importRecap: null,
      createdList: null,
    };
  },
  computed: {
    ...mapGetters(["activeLists"]),
    /**
     * Image d'exemple du fichier Excel, adaptée à la langue de l'utilisateur.
     * @returns {String} Chemin vers l'image.
     */
    excelRapideImage() {
      return this.$i18n.locale === "de" ? excelRapideImageDe : excelRapideImageFr;
    },
    modalTitle() {
      if (this.modeFile && this.importRecap) {
        return this.$t("liste.la-liste-a-bien-ete-creee");
      }
      return this.$t("liste.creer-nouvelle-liste");
    },
    modalSubtitle() {
      if (this.modeFile && this.importRecap && this.createdList?.libelle) {
        return this.createdList.libelle;
      }
      return this.listName || this.uniqueLibelle;
    },
    uniqueLibelle() {
      let index = 1;
      let libelle = `${this.$t("liste.liste-rapide-x")} ${index}`;

      while (this.libelleExists(libelle)) {
        index += 1;
        libelle = `${this.$t("liste.liste-rapide-x")} ${index}`;
      }
      return libelle;
    },
  },
  methods: {
    moneyFilter,
    /**
     * Vérifie l'existence du libellé donné parmi les libellés des listes de l'utilisateur.
     * @param {String} libelle libelle de la liste à vérifier.
     */
    libelleExists(libelle) {
      return this.activeLists.some((list) => list.libelle === libelle);
    },
    /**
     * Remet les valeurs par défaut une fois la modale fermée.
     */
    hide() {
      this.$modal.hide("modal_listes_saisie_rapide");
      this.lignes = [{}, {}, {}, {}];
      this.resetFileMode();
      this.createdList = null;
    },
    /**
     * Bascule entre saisie manuelle et import fichier.
     */
    toggleMode() {
      this.modeFile = !this.modeFile;
      this.importRecap = null;
      this.createdList = null;
    },

    goToCreatedList() {
      if (!this.createdList?.id) {
        return;
      }

      this.$router.push({
        name: "listes_devis_listes_item",
        params: { id: this.createdList.id },
      });
      this.hide();
    },
    /**
     * Ouvre la fenêtre de sélection de fichier.
     */
    openFilePicker() {
      this.$refs.fileInput?.click();
    },
    /**
     * Active l'état visuel de survol lors du drag and drop.
     */
    handleDragOver() {
      this.isDragOver = true;
    },
    /**
     * Désactive l'état visuel de survol lors de la sortie de la dropzone.
     */
    handleDragLeave() {
      this.isDragOver = false;
    },
    /**
     * Gère le dépôt d'un fichier dans la dropzone.
     * @param {DragEvent} event Évènement de dépôt.
     */
    handleDrop(event) {
      this.isDragOver = false;
      const [file] = event.dataTransfer?.files || [];
      if (file) {
        this.selectedFile = file;
      }
    },
    /**
     * Gère la sélection d'un fichier via l'input natif.
     * @param {Event} event Évènement de sélection.
     */
    handleFileInput(event) {
      const [file] = event.target?.files || [];
      if (file) {
        this.selectedFile = file;
      }
    },
    /**
     * Réinitialise l'état du mode fichier.
     */
    resetFileMode() {
      this.modeFile = false;
      this.selectedFile = null;
      this.isDragOver = false;
      this.importRecap = null;
      this.createdList = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },
    /**
     * Trie les éléments du tableau.
     * @param {String} sortKey Nom du champ sur lequel trier les offres.
     */
    sortOffers(sortKey) {
      const filledLines = [];
      const emptyLines = [];

      this.lignes.forEach((ligne) => {
        if (Object.keys(ligne).includes(sortKey)) {
          filledLines.push(ligne);
        } else {
          emptyLines.push(ligne);
        }
      });

      if (this.lastSort.key !== sortKey) {
        // Si le dernier tri n'est pas sur la même colonne, on trie par défaut
        filledLines.sort((a, b) => {
          if (a.offre && b.offre) {
            if (a.offre[sortKey] < b.offre[sortKey]) return -1;
            if (a.offre[sortKey] > b.offre[sortKey]) return 1;
          }
          return 0;
        });
        this.lastSort.asc = true;
      } else {
        // Si le dernier tri est sur la même colonne, on effectue l'inverse du précédent tri
        filledLines.reverse();
        this.lastSort.asc = !this.lastSort.asc;
      }
      this.lastSort.key = sortKey;
      this.lignes = [...filledLines, ...emptyLines];
    },
    /**
     * Récupère les offres avec ses informations selon le texte tapé.
     * @param {String} searchText Texte tapé dans l'input.
     * @param {String} ligne Ligne sur laquelle on recherche une offre.
     */
    fetchOffres(searchText, ligne) {
      this.optionsAreLoading = true;
      const searchtextFormatted = searchText.replaceAll("-", "").replaceAll(" ", "");

      Api().get(`/recherche/offre/?search=${searchtextFormatted}`)
        .then(({ data }) => {
          this.options = data.results.map((res) => ({
            label: `${res.ean}_${res.ref}`,
            value: `${res.ean}_${res.ref}`,
            id: res.id,
            ean: res.ean,
            libelle: res.produit.libelle,
            prix_public: res.prix_public,
            dispo: this.couleurSelonDisponibilite(res.produit),
            dispo_libelle: this.libelleSelonDisponibilite(res.produit),
            produit_remplacement: res.produit.produit_remplacement,
          }));
          this.optionsAreLoading = false;
          if (this.options.length === 1 && this.options[0].label.length === searchtextFormatted.length) {
            const index = this.lignes.indexOf(ligne);
            this.lignes[index].ean = this.options[0];
            this.handleSelect(ligne);
          }
        });
    },
    /**
     * Permet de définir le comportement de la ligne lorsqu'un produit est sélectionné.
     * @param {Object} selectedLine Ligne sélectionnée.
     * @param {Number} index Index de la ligne sélectionnée.
     */
    handleSelect(selectedLine, index) {
      // Remplace le produit indisponible par sa dernière version si elle est disponible
      const oldEan = { ...selectedLine }.offre.ean;
      const produitRemplacement = selectedLine.offre.produit_remplacement;

      if (selectedLine.offre?.dispo === "red" && this.couleurSelonDisponibilite(produitRemplacement) !== "red") {
        this.lignes[index].offre = {
          label: `${produitRemplacement.ean}_${produitRemplacement.ref_editeur}`,
          value: `${produitRemplacement.ean}_${produitRemplacement.ref_editeur}`,
          id: produitRemplacement.id,
          ean: produitRemplacement.ean,
          libelle: produitRemplacement.libelle,
          prix_public: produitRemplacement.offre_defaut.prix_editeur,
          dispo: this.couleurSelonDisponibilite(produitRemplacement),
          dispo_libelle: this.libelleSelonDisponibilite(produitRemplacement),
          eanBase: oldEan,
        };
      }

      // Met la quantité à 1 par défaut
      if (!selectedLine.quantite) {
        selectedLine.quantite = 1;
      }
    },
    /**
     * Indique si la ligne donnée bloque la création de la liste car son offre est indisponible.
     * @param {Object} ligne Ligne du tableau.
     * @returns {Boolean} true si la ligne contient une offre indisponible.
     */
    isLigneIndisponible(ligne) {
      return ligne?.offre?.dispo === "red";
    },
    /**
     * Supprime ou vide une ligne du tableau.
     * @param {Number} index Index de la ligne à supprimer ou à vider.
     */
    deleteLine(index) {
      if (this.lignes.length > 4) {
        this.lignes.splice(index, 1);
      } else {
        this.lignes[index] = {};
      }
    },
    /**
     * Créer une nouvelle liste avec les lignes de la modale.
     */
    async createList() {
      this.isCreatingList = true;
      this.importRecap = null;
      const lignesIndisponibles = this.lignes
        .map((ligne, index) => ({ ligne, numero: index + 1 }))
        .filter(({ ligne }) => this.isLigneIndisponible(ligne));

      if (lignesIndisponibles.length) {
        // On détaille les lignes bloquantes pour que l'utilisateur sache lesquelles supprimer
        const details = lignesIndisponibles
          .map(({ ligne, numero }) => this.$t("liste.ligne-x-ean-y-motif-z", {
            numero,
            ean: ligne.offre.label,
            motif: ligne.offre.dispo_libelle || this.$t("produit.indisponible"),
          }))
          .join("<br />");

        this.$toast.error({
          title: this.$t("produit.impossible-ajouter-produits-indispo"),
          content: `${this.$t("liste.supprimez-lignes-indispo-pour-creer-liste")}<br />${details}`,
        });
        this.isCreatingList = false;
      } else {
        try {
          const { data } = await Api().post("/liste/", { libelle: this.listName || this.uniqueLibelle });
          this.createdList = data;

          if (!this.selectedFile) {
            const linesToAdd = this.lignes.filter((ligne) => ligne.offre).map((line) => ({
              id_offre: line?.offre.id,
              quantite: parseInt(line.quantite, 10),
            }));

            // Supprime les doublons du tableau d'objets et additionne les quantités en cas de doublons
            const linesToAddDistinct = linesToAdd.reduce((acc, item) => {
              const existingItem = acc.find((other) => other.id_offre === item.id_offre);
              if (existingItem) {
                existingItem.quantite += item.quantite;
              } else {
                acc.push(item);
              }
              return acc;
            }, []);

            await Api().post(`/liste/${data.id}/add_lignes/`, { lignes: linesToAddDistinct });
            this.$toast.success({ title: this.$t("liste.liste-x-bien-creee", { libelle: data.libelle }) });

            if (window.resolveCreateListPromise) {
              window.resolveCreateListPromise();
            }

            this.goToCreatedList();
            return;
          }

          try {
            const formData = new FormData();
            formData.append("file", this.selectedFile);
            const importResponse = await Api().post(`/liste/${data.id}/import_lignes/`, formData, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            });

            const notFoundEans = importResponse?.data?.not_found_eans || [];
            const importedCount = importResponse?.data?.imported_count || 0;
            if (notFoundEans.length) {
              this.importRecap = {
                listLabel: data.libelle,
                importedCount,
                notFoundCount: notFoundEans.length,
                notFoundEans,
              };
              return;
            }

            this.$toast.success({ title: this.$t("liste.liste-x-bien-creee", { libelle: data.libelle }) });

            if (window.resolveCreateListPromise) {
              window.resolveCreateListPromise();
            }

            this.goToCreatedList();
          } catch (err) {
            const payload = err.response?.data;
            if (payload?.not_found_eans?.length) {
              this.importRecap = {
                listLabel: data.libelle,
                message: payload.message || "Aucune offre trouvée dans le fichier importé.",
                notFoundCount: payload.not_found_count || payload.not_found_eans.length,
                notFoundEans: payload.not_found_eans,
              };
              return;
            }

            this.$toast.error({ title: payload || this.$t("info.une-erreur-est-survenue") });
          }
        } catch (err) {
          const payload = err.response?.data;
          this.$toast.error({ title: payload || this.$t("info.une-erreur-est-survenue") });
        } finally {
          this.isCreatingList = false;
        }
      }
    },
  },
};
</script>

<style lang="scss">
@use "@/assets/styles/components/modals/modal_listes_saisie_rapide.scss";
</style>
